import React, { useState, useEffect } from 'react';
import { Img } from 'react-image';
import { getTheme, setTheme, type Theme } from '../../utils/theme';
import { IconUser, IconMemo, IconChart, IconLock } from '../../components/Icons';
import UserIdForm from '../../components/UserIdForm';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
  uuid: string;
  nickname: string;
  setNickname: (nick: string) => void;
  /** 로그인 아이디 — [아이디 변경]이 지금 아이디를 보여 주고, 바꾸면 setUserId 로 알린다 */
  userId?: string;
  setUserId?: (id: string) => void;
  profileImg: string | null;
  setProfileImg: (img: string | null) => void;
  onLogout: () => void;
  /**
   * 다른 화면으로 가기 — `records` 는 내 전투기록(길드원 탭의 그 사람 기록 창), `stats` 는 통계 탭 [길드원 통계] 의 내 통계.
   * 주지 않으면 이동 메뉴를 그리지 않는다.
   */
  onNavigate?: (to: 'records' | 'stats') => void;
}

/**
 * **내 프로필 창** (오른쪽 위 프로필 단추).
 *
 * 예전에는 어두운 남색 판을 박아 두었다. 다크모드는 화면의 인라인 색 **밝기만 뒤집는** 방식이라
 * (utils/theme.ts) 어두운 판은 밝은 화면에서는 혼자 튀고, 다크모드에서는 다른 화면과 결이 달랐다
 * (사용자 지시: 두 모드 모두 디자인을 바꿔 달라). 그래서 **사이트의 다른 카드와 같은 밝은 판**으로
 * 그린다 — 다크모드에서는 다른 화면처럼 알아서 어두워진다. 보라 강조색은 두 모드에서 그대로 남는다.
 *
 * 맨 위 프로필, 그 아래 메뉴(내 전투기록 · 내 통계 · 아이디 변경 · 다크모드), 맨 아래 로그아웃.
 */
const ProfileModal: React.FC<ProfileModalProps> = ({ open, onClose, nickname, userId, setUserId, profileImg, onLogout, onNavigate }) => {
  const [imgPreview, setImgPreview] = useState<string | null>(profileImg);
  /** 다크모드. 켜고 끈 값은 이 브라우저에 남는다(utils/theme.ts) */
  const [theme, setThemeState] = useState<Theme>(getTheme());
  /**
   * [아이디 변경] — 줄을 누르면 그 아래에 지금 아이디 · 새 아이디 칸 · [변경] 이 펼쳐진다 (사용자 2026-10-27 "개인 프로필 클릭해서 변경할 수 있게").
   * 이 사이트는 아이디만으로 로그인하므로 아이디가 비밀번호 몫이다 — 지금 접속은 그대로, 다음 로그인부터 새 아이디.
   */
  const [idOpen, setIdOpen] = useState(false);

  useEffect(() => {
    setImgPreview(profileImg);
  }, [profileImg]);

  if (!open) return null;

  const avatar = (
    <div style={{
      width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
      background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30
    }}>
      {imgPreview ? (
        <Img
          src={imgPreview}
          unloader={<IconUser />}
          alt="프로필"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : <IconUser />}
    </div>
  );

  /** 메뉴 한 줄 — 왼쪽 아이콘 칸, 이름, 오른쪽 끝(꺾쇠나 스위치) */
  const row = (key: string, icon: React.ReactNode, label: string, right: React.ReactNode, onClick?: () => void) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 12px', border: 'none', borderRadius: 12,
        background: 'transparent', cursor: onClick ? 'pointer' : 'default', textAlign: 'left'
      }}
      onMouseEnter={(e) => { if (onClick) e.currentTarget.style.background = 'rgba(102, 126, 234, 0.08)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <span style={{
        width: 34, height: 34, borderRadius: 10, flexShrink: 0,
        background: 'rgba(102, 126, 234, 0.12)', color: '#667eea',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17
      }}>{icon}</span>
      <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: '#333' }}>{label}</span>
      {right}
    </button>
  );

  const chevron = <span style={{ color: '#b0b8c4', fontSize: 18, fontWeight: 700, lineHeight: 1 }}>›</span>;

  const darkSwitch = (
    <span
      aria-hidden
      style={{
        width: 46, height: 26, borderRadius: 13, padding: 3, flexShrink: 0, boxSizing: 'border-box',
        display: 'flex', justifyContent: theme === 'dark' ? 'flex-end' : 'flex-start', alignItems: 'center',
        background: theme === 'dark' ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#d5dae1',
        transition: 'background 0.2s'
      }}
    >
      {/* 손잡이는 SVG 로 — 다크모드는 밝은 인라인 바탕을 어둡게 칠해서, 흰 동그라미가 바탕에 묻혔다 */}
      <svg width="20" height="20" viewBox="0 0 20 20" style={{ display: 'block', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}>
        <circle cx="10" cy="10" r="10" fill="#ffffff" />
      </svg>
    </span>
  );

  const toggleDark = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setThemeState(next);
  };

  const go = (to: 'records' | 'stats') => {
    onNavigate?.(to);
    onClose();
  };

  /** [아이디 변경] 펼침 — 지금 아이디 · 새 아이디 칸 · [변경] · 결과 */
  const idPanel = (
    <div style={{ margin: '2px 6px 8px 52px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 13, color: '#667085', fontWeight: 600 }}>
        지금 아이디 <span style={{ color: '#222', fontWeight: 800, marginLeft: 4, wordBreak: 'break-all' }}>{userId || '-'}</span>
      </div>
      {/* 새 아이디 칸 · [변경] · 결과 — 접으면 칸·문구도 지워진다(다시 펼치면 새로) */}
      <UserIdForm current={userId} onChanged={(id) => setUserId?.(id)} />
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh',
        background: 'rgba(15, 20, 35, 0.45)', zIndex: 300,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)', animation: 'pmFade 0.18s ease-out'
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes pmFade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pmUp { from { opacity: 0; transform: translateY(14px) scale(0.97); } to { opacity: 1; transform: none; } }
      `}</style>
      <div
        style={{
          background: '#ffffff', borderRadius: 20, width: 'min(92vw, 360px)', boxSizing: 'border-box',
          padding: '20px 16px 16px', position: 'relative',
          boxShadow: '0 18px 50px rgba(30, 40, 80, 0.25)', border: '1px solid #e8ecf3',
          animation: 'pmUp 0.22s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 닫기 */}
        <button
          type="button"
          onClick={onClose}
          aria-label="닫기"
          style={{
            position: 'absolute', top: 12, right: 12, width: 32, height: 32, borderRadius: 10,
            border: 'none', background: '#f1f3f7', color: '#8a94a6', fontSize: 18, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
        >×</button>

        {/* 프로필 — 얼굴은 게임 화면에서 잘라 올리고, 닉네임은 게임을 따라간다(사이트에서 바꾸지 않는다) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 6px 16px' }}>
          {avatar}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 19, fontWeight: 800, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nickname}</div>
            <div style={{ fontSize: 12, color: '#98a2b3', fontWeight: 600, marginTop: 3 }}>게임 닉네임을 자동으로 따라갑니다</div>
          </div>
        </div>

        <div style={{ height: 1, background: '#eef1f5', margin: '0 4px 8px' }} />

        {/* 메뉴 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {onNavigate && row('records', <IconMemo />, '내 전투기록', chevron, () => go('records'))}
          {onNavigate && row('stats', <IconChart />, '내 통계', chevron, () => go('stats'))}
          {setUserId && row('userid', <IconLock />, '아이디 변경',
            <span style={{ color: '#b0b8c4', fontSize: 18, fontWeight: 700, lineHeight: 1, display: 'inline-block', transform: idOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>›</span>,
            () => setIdOpen((v) => !v))}
          {setUserId && idOpen && idPanel}
          {row('dark', <span style={{ fontWeight: 900 }}>☾</span>, '다크모드', darkSwitch, toggleDark)}
        </div>

        <div style={{ height: 1, background: '#eef1f5', margin: '8px 4px 14px' }} />

        {/* 로그아웃 */}
        <button
          type="button"
          onClick={() => { onLogout(); onClose(); }}
          style={{
            width: '100%', padding: '12px 0', borderRadius: 12, cursor: 'pointer',
            border: '1px solid rgba(229, 57, 53, 0.35)', background: 'rgba(229, 57, 53, 0.06)',
            color: '#e53935', fontWeight: 800, fontSize: 15
          }}
        >로그아웃</button>
      </div>
    </div>
  );
};

export default ProfileModal;
